import React, { useMemo } from 'react';
import {
  BsDownload,
  BsArrowRight,
  BsArrowLeft,
  BsBarChartLine,
  BsCamera,
  BsGraphUpArrow,
  BsBoxArrowInDown,
  BsPersonCheck,
} from 'react-icons/bs';
import { FaWindows, FaApple, FaLinux } from 'react-icons/fa';
import { TbLoader2 } from 'react-icons/tb';
import classNames from 'classnames';

import { useDesktopAppDownloadUrls } from '@hooks/useDesktopAppDownloadUrls';
import styles from '../Onboarding.module.scss';

// ── OS Detection ──────────────────────────────────────────────

const detectOS = () => {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macos';
  if (/Linux/i.test(ua)) return 'linux';
  return 'windows';
};

// ── Constants ─────────────────────────────────────────────────

// Static per-platform presentation; downloadUrl/url is merged in at render
// time from useDesktopAppDownloadUrls (Firestore-backed, staff-editable).
const PLATFORM_META = {
  windows: {
    key: 'windows',
    icon: <FaWindows size={34} color="#0078d4" />,
    iconBg: '#e8f4ff',
    detectedLabel: 'Windows Detected',
    desc: 'Best performance for your device',
    btnLabel: 'Download for Windows',
  },
  macos: {
    key: 'macos',
    icon: <FaApple size={34} color="#1a1a1a" />,
    iconBg: '#f2f4f7',
    detectedLabel: 'MacOS Detected',
    desc: 'Best performance for your device',
    btnLabel: 'Download for macOS',
  },
  linux: {
    key: 'linux',
    icon: <FaLinux size={34} color="#1a1a1a" />,
    iconBg: '#f2f4f7',
    detectedLabel: 'Linux Detected',
    desc: 'Best performance for your device',
    btnLabel: 'Download for Linux',
  },
};

const OTHER_PLATFORMS_META = {
  windows: [
    {
      key: 'macos',
      icon: <FaApple size={22} color="#1a1a1a" />,
      name: 'macOS',
      desc: 'For macOS',
    },
    {
      key: 'linux',
      icon: <FaLinux size={22} color="#1a1a1a" />,
      name: 'Linux',
      desc: 'For Ubuntu, Debian and other distros',
    },
  ],
  macos: [
    {
      key: 'windows',
      icon: <FaWindows size={22} color="#0078d4" />,
      name: 'Windows',
      desc: 'For Windows',
    },
    {
      key: 'linux',
      icon: <FaLinux size={22} color="#1a1a1a" />,
      name: 'Linux',
      desc: 'For Ubuntu, Debian and other distros',
    },
  ],
  linux: [
    {
      key: 'windows',
      icon: <FaWindows size={22} color="#0078d4" />,
      name: 'Windows',
      desc: 'For Windows 10 and above',
    },
    {
      key: 'macos',
      icon: <FaApple size={22} color="#1a1a1a" />,
      name: 'macOS',
      desc: 'For macOS 11+ (Big Sur and above)',
    },
  ],
};

const FEATURES = [
  {
    icon: <BsBarChartLine size={16} color="#3363F5" />,
    title: 'Real-time activity tracking',
    desc: 'Monitor apps, websites and productivity',
  },
  {
    icon: <BsCamera size={16} color="#039855" />,
    title: 'Automatic screenshots',
    desc: 'Secure and encrypted data capture',
  },
  {
    icon: <BsGraphUpArrow size={16} color="#6938EF" />,
    title: 'Workforce insights',
    desc: 'Get actionable reports and analytics',
  },
];

const INSTALL_STEPS = [
  {
    icon: <BsDownload size={22} color="#3363F5" />,
    title: 'Download App',
    desc: 'Get the desktop application',
  },
  {
    icon: <BsBoxArrowInDown size={22} color="#039855" />,
    title: 'Install',
    desc: 'Run the installer and follow steps',
  },
  {
    icon: <BsPersonCheck size={22} color="#6938EF" />,
    title: 'Sign In',
    desc: 'Use your Prodaff account',
  },
  {
    icon: <BsGraphUpArrow size={22} color="#F79009" />,
    title: 'Start Tracking',
    desc: 'Activity tracking begins automatically',
  },
];

const LAPTOP_BAR_HEIGHTS = [30, 55, 72, 45, 62, 85, 50];
const LAPTOP_ACCENT_IDX = 5;
const LAPTOP_NAV = [false, true, false, false, false];

// ── Sub-components ────────────────────────────────────────────

const LaptopMockup = () => (
  <div className={styles.ddaLaptopMockup}>
    <div className={styles.ddaLaptopLid}>
      <div className={styles.ddaLaptopScreen}>
        <div className={styles.ddaLaptopSidebar}>
          {LAPTOP_NAV.map((active, i) => (
            <div
              key={i}
              className={classNames(styles.ddaLaptopNavDot, { active })}
            />
          ))}
        </div>
        <div className={styles.ddaLaptopContent}>
          <div className={styles.ddaLaptopTopBar} />
          <div className={styles.ddaLaptopStatRow}>
            <div className={styles.ddaLaptopStat} />
            <div className={styles.ddaLaptopStat} />
            <div className={styles.ddaLaptopStat} />
          </div>
          <div className={styles.ddaLaptopChartRow}>
            <div className={styles.ddaLaptopBars}>
              {LAPTOP_BAR_HEIGHTS.map((h, i) => (
                <div
                  key={i}
                  className={classNames(styles.ddaLaptopBar, {
                    accent: i === LAPTOP_ACCENT_IDX,
                  })}
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
            <div className={styles.ddaLaptopDonut} />
          </div>
        </div>
      </div>
    </div>
    <div className={styles.ddaLaptopBase} />
    <div className={styles.ddaLaptopStand} />
  </div>
);

// ── Main component ────────────────────────────────────────────

export const DownloadDesktopApp = ({ onFinish, onBack, loading }) => {
  const detectedOS = useMemo(detectOS, []);
  const { downloadUrls } = useDesktopAppDownloadUrls();

  const primary = useMemo(
    () => ({
      ...PLATFORM_META[detectedOS],
      downloadUrl: downloadUrls[detectedOS]?.downloadUrl,
      currentVersion: downloadUrls[detectedOS]?.currentVersion,
    }),
    [detectedOS, downloadUrls]
  );
  const others = useMemo(
    () =>
      OTHER_PLATFORMS_META[detectedOS].map((p) => ({
        ...p,
        url: downloadUrls[p.key]?.downloadUrl,
      })),
    [detectedOS, downloadUrls]
  );

  return (
    <div className={styles.ddaPage}>
      {/* Hero */}
      <div className={styles.ddaHero}>
        <div className={styles.ddaHeroText}>
          <h1 className={styles.ddaHeroTitle}>One last step!</h1>
          <p className={styles.ddaHeroSubtitle}>
            Install Prodaff Desktop App to start monitoring, tracking and unlock
            powerful productivity insights.
          </p>
        </div>
        <LaptopMockup />
      </div>

      {/* Recommended for you */}
      <div className={styles.ddaSection}>
        <h3 className={styles.ddaSectionTitle}>Recommended for you</h3>
        <div className={styles.ddaRecommendedCard}>
          {/* Primary platform */}
          <div className={styles.ddaRecommendedLeft}>
            <div
              className={styles.ddaRecommendedIconWrap}
              style={{ backgroundColor: primary.iconBg }}
            >
              {primary.icon}
            </div>
            <div className={styles.ddaRecommendedMeta}>
              <div className={styles.ddaRecommendedTitleRow}>
                <h3 className={styles.ddaRecommendedTitle}>
                  {primary.detectedLabel}
                </h3>
                <span className={styles.ddaRecommendedBadge}>Recommended</span>
              </div>
              <p className={styles.ddaRecommendedDesc}>{primary.desc}</p>
              {primary.downloadUrl ? (
                <a
                  href={primary.downloadUrl}
                  className={styles.ddaPrimaryDownloadBtn}
                  rel="noopener noreferrer"
                >
                  <BsDownload size={14} />
                  {primary.btnLabel}
                </a>
              ) : (
                <button
                  type="button"
                  className={styles.ddaPrimaryDownloadBtn}
                  disabled
                >
                  <BsDownload size={14} />
                  {primary.btnLabel}
                </button>
              )}
              {primary.currentVersion && (
                <p className={styles.ddaVersionMeta}>
                  Version {primary.currentVersion}
                </p>
              )}
            </div>
          </div>

          {/* Feature bullets */}
          <div className={styles.ddaRecommendedRight}>
            {FEATURES.map((feat) => (
              <div key={feat.title} className={styles.ddaFeatureItem}>
                <div className={styles.ddaFeatureIconWrap}>{feat.icon}</div>
                <div className={styles.ddaFeatureText}>
                  <span className={styles.ddaFeatureTitle}>{feat.title}</span>
                  <span className={styles.ddaFeatureDesc}>{feat.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Other platforms */}
      <div className={styles.ddaSection}>
        <h3 className={styles.ddaSectionTitle}>Other platforms</h3>
        <div className={styles.ddaPlatformCards}>
          {others.map((p) => (
            <div key={p.key} className={styles.ddaPlatformCard}>
              <div className={styles.ddaPlatformIconRow}>
                <div className={styles.ddaPlatformIconWrap}>{p.icon}</div>
                <h4 className={styles.ddaPlatformName}>{p.name}</h4>
              </div>
              <p className={styles.ddaPlatformDesc}>{p.desc}</p>
              {p.url ? (
                <a
                  href={p.url}
                  className={styles.ddaOutlineBtn}
                  rel="noopener noreferrer"
                >
                  Download
                </a>
              ) : (
                <span
                  className={classNames(styles.ddaOutlineBtn, styles.disabled)}
                >
                  Download
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Installation steps */}
      <div className={styles.ddaSection}>
        <h3 className={styles.ddaSectionTitle}>Installation steps</h3>
        <div className={styles.ddaSteps}>
          {INSTALL_STEPS.map((step, i) => (
            <React.Fragment key={step.title}>
              <div className={styles.ddaStep}>
                <div className={styles.ddaStepIconWrap}>{step.icon}</div>
                <span className={styles.ddaStepTitle}>{step.title}</span>
                <p className={styles.ddaStepDesc}>{step.desc}</p>
              </div>
              {i < INSTALL_STEPS.length - 1 && (
                <BsArrowRight size={18} className={styles.ddaStepArrow} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Footer actions */}
      <div className={styles.ddaFooter}>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          <BsArrowLeft size={16} />
          Back
        </button>
        <button
          type="button"
          className={styles.ddaOpenDashboardBtn}
          onClick={onFinish}
          disabled={loading}
        >
          {loading ? (
            <TbLoader2 size={16} className={styles.continueBtnSpinner} />
          ) : (
            <>
              Open Dashboard
              <BsArrowRight size={15} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
